import { Flex } from "antd";
import { useCallback, useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
    getPointGetByExternalId,
    getRouteFindAllByPointId,
} from "tm-shared/api";
import { Option, PointType } from "tm-shared/types";
import { validationMessage } from "tm-shared/utils";

import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { RHFAutocomplete } from "@/shared/forms/RHF/RHFAutocomplete";
import { RHFTextField } from "@/shared/forms/RHF/RHFTextField";
import { PrimaryText } from "@/shared/ui/Typography";
import { setPointCreatePageModal } from "@/store/slices/ui";
import { NewPointFormValues } from "@/types/form";
import { PointCreateModal } from "@/types/modals";

import {
    FieldName,
    SUCCESS_ERR_CODE_CHECK,
} from "../CreatePointForm.constants";

const DEFAULT_PARTNER_ID = "alcoline";

interface PartnerSectionProps {
    canEditPartner: boolean;
    activeMap: Record<FieldName, boolean>;
    resetBelow: (field: FieldName) => void;
    setIsCheckingPartnerExternalId: (value: boolean) => void;
    setVerifiedPartnerExternalId: (value: string | null) => void;
    isCheckingPartnerExternalId: boolean;
    partners: Option[];
}

export const PartnerSection = ({
    canEditPartner,
    activeMap,
    resetBelow,
    setIsCheckingPartnerExternalId,
    setVerifiedPartnerExternalId,
    isCheckingPartnerExternalId,
    partners,
}: PartnerSectionProps) => {
    // Store data
    const { employee } = useAppSelector((state) => state.user);

    // Derived values
    const partnerId = employee?.context?.clientId ?? DEFAULT_PARTNER_ID;
    const partnerLabel =
        partners.find((partner) => partner.value === partnerId)?.label ??
        partnerId;

    // Form hooks
    const dispatch = useAppDispatch();
    const { clearErrors, setError, getValues, setValue } =
        useFormContext<NewPointFormValues>();
    const pointType = useWatch<NewPointFormValues, "pointType">({
        name: "pointType",
    });

    // Refs
    const debounceTimeoutReference = useRef<ReturnType<
        typeof setTimeout
    > | null>(null);

    // External ID
    const validateExternalId = useCallback(
        async (externalId?: string) => {
            if (!externalId || externalId.length < 3) return;

            setIsCheckingPartnerExternalId(true);

            try {
                const pointResponse = await getPointGetByExternalId({
                    externalId,
                    ownerId: partnerId,
                });

                const routesResponse = await getRouteFindAllByPointId({
                    pointId: pointResponse.result.id,
                });

                dispatch(
                    setPointCreatePageModal({
                        type: PointCreateModal.DuplicateById,
                        payload: {
                            point: pointResponse.result,
                            routeCount: routesResponse.result.length,
                        },
                    }),
                );

                setError("clientExternalPointId", {
                    message: "Маркет із таким ID вже існує",
                });
                setVerifiedPartnerExternalId(null);
            } catch (error) {
                if (error && typeof error === "object" && "code" in error) {
                    if (error.code !== SUCCESS_ERR_CODE_CHECK) {
                        setError("clientExternalPointId", {
                            message: "Помилка при перевірці маркету",
                        });
                        setVerifiedPartnerExternalId(null);
                    } else {
                        clearErrors("clientExternalPointId");
                        setVerifiedPartnerExternalId(
                            getValues("clientExternalPointId") === externalId
                                ? externalId
                                : null,
                        );
                    }
                }
            } finally {
                setIsCheckingPartnerExternalId(false);
            }
        },
        [
            clearErrors,
            dispatch,
            getValues,
            partnerId,
            setError,
            setIsCheckingPartnerExternalId,
            setVerifiedPartnerExternalId,
        ],
    );

    const debouncedValidateExternalId = useCallback(
        async (externalId?: string) => {
            if (debounceTimeoutReference.current) {
                clearTimeout(debounceTimeoutReference.current);
            }

            if (!externalId || externalId.length < 3) {
                setIsCheckingPartnerExternalId(false);
                setVerifiedPartnerExternalId(null);
                return;
            }

            setVerifiedPartnerExternalId(null);

            await new Promise<void>((resolve) => {
                debounceTimeoutReference.current = setTimeout(() => {
                    void validateExternalId(externalId).finally(resolve);
                }, 1500);
            });
        },
        [
            setIsCheckingPartnerExternalId,
            setVerifiedPartnerExternalId,
            validateExternalId,
        ],
    );

    // Effects
    useEffect(() => {
        return () => {
            if (debounceTimeoutReference.current) {
                clearTimeout(debounceTimeoutReference.current);
            }
        };
    }, []);

    useEffect(() => {
        if (pointType !== PointType.Distribution) return;

        const externalId = getValues("clientExternalPointId");

        void validateExternalId(externalId);
    }, [getValues, pointType, validateExternalId]);

    useEffect(() => {
        if (pointType !== PointType.Distribution) return;

        setVerifiedPartnerExternalId(null);

        setValue("partner", partnerId, {
            shouldValidate: true,
            shouldDirty: false,
        });
    }, [partnerId, pointType, setValue, setVerifiedPartnerExternalId]);

    // Render guards
    if (pointType !== PointType.Distribution) {
        return null;
    }

    // Render
    return (
        <>
            {!canEditPartner ? (
                <Flex vertical gap={4}>
                    <PrimaryText>Партнер:</PrimaryText>
                    <PrimaryText fontWeight="700">{partnerLabel}</PrimaryText>
                </Flex>
            ) : (
                <RHFAutocomplete
                    name="partner"
                    options={partners}
                    label="Партнер"
                    disabled={!activeMap.partner}
                    onAfterChange={() => resetBelow("partner")}
                    rules={{
                        required: validationMessage.required,
                    }}
                    allowClear={false}
                    selectProps={{ size: "large" }}
                />
            )}

            <RHFTextField
                name="clientExternalPointId"
                label="ID торгової точки в обліковому записі партнера"
                disabled={!activeMap.clientExternalPointId}
                onAfterChange={() => resetBelow("clientExternalPointId")}
                onAfterChangeAsync={debouncedValidateExternalId}
                loading={isCheckingPartnerExternalId}
                size="large"
                rules={{
                    minLength: {
                        value: 3,
                        message: validationMessage.minLength(3),
                    },
                    required: validationMessage.required,
                }}
            />
        </>
    );
};
